"use client";

import { forwardRef } from "react";
import { ArrowRight } from "lucide-react";

import FeatureAnnouncement from "@/components/FeatureAnnouncement";
import SabineLead from "@/components/SabineLead";
import {
  NAV_CORAL_MARK,
  NAV_CORAL_TEXT,
  NAV_FOCUS_RING,
  NAV_ICON_BUTTON,
  NAV_INK_FOCUS_RING,
} from "@/components/nav/nav-styles";
import { useAskStudioAnnouncement } from "@/hooks/useAskStudioAnnouncement";
import { SABINE_ANNOUNCEMENT_TITLE, SABINE_NAME } from "@/lib/sabine";
import { cn } from "@/lib/utils";

export const COACH_LAUNCHER_TEST_ID = "ask-studio-launcher";

const ANNOUNCEMENT = `Ask ${SABINE_NAME} about whatever page you are on: this playbook, a result, a report. She opens beside the menu, so the page stays in view.`;

const LAUNCHER_SHAPE =
  "w-auto max-w-md gap-2.5 pl-1.5 pr-3 text-sm font-medium";

// Two surfaces. On the navy top bar a faint white tint and hairline make it
// a button beside the white search field, never a second field. On the cream
// strip of a signed-out page it is the cream ghost button.
const SURFACE = {
  ink: cn(
    "flex h-9 min-w-0 items-center rounded-lg bg-white/5 text-white ring-1 ring-inset ring-white/10 transition-colors hover:bg-white/10",
    NAV_INK_FOCUS_RING,
  ),
  cream: cn(NAV_ICON_BUTTON, NAV_FOCUS_RING, "text-gray-900"),
};
const VERB = { ink: NAV_CORAL_MARK, cream: NAV_CORAL_TEXT };

/**
 * The closed chat: Sabine's face, the page's own question, and "Ask" as the
 * verb. It opens a chat, so it is a button, not a field. Clicking it opens
 * the chat beside the rail, where the composer takes focus. `ink` seats it on
 * the navy top bar; without it, it sits on cream.
 *
 * @param {object} props
 * @param {string} props.placeholder  What this page lets you ask; the chat's
 *   composer says the same
 * @param {() => void} props.onClick
 * @param {boolean} [props.ink]
 */
const CoachLauncher = forwardRef(function CoachLauncher(
  { placeholder, onClick, ink = false },
  ref,
) {
  const surface = ink ? "ink" : "cream";
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      aria-label={`Ask ${SABINE_NAME}: ${placeholder}`}
      data-testid={COACH_LAUNCHER_TEST_ID}
      className={cn(SURFACE[surface], LAUNCHER_SHAPE)}
    >
      <SabineLead size="sm" />
      <span className="truncate">{placeholder}</span>
      <span
        className={cn(
          "flex shrink-0 items-center gap-1 font-semibold",
          VERB[surface],
        )}
      >
        Ask
        <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </span>
    </button>
  );
});

export default CoachLauncher;

/**
 * The launcher with the "Meet Sabine" announcement pointing at it, shown once
 * for the whole app the first time anyone sees an Ask button. Its own
 * component so the announcement's on-screen check runs when the button
 * mounts, not when the bar around it does.
 */
export function AskSabine({ placeholder, onOpen, ink = false }) {
  const { shouldShow, markAsSeen, targetRef } = useAskStudioAnnouncement();
  return (
    <>
      <CoachLauncher
        ref={targetRef}
        placeholder={placeholder}
        onClick={onOpen}
        ink={ink}
      />
      <FeatureAnnouncement
        targetRef={targetRef}
        title={SABINE_ANNOUNCEMENT_TITLE}
        description={ANNOUNCEMENT}
        isOpen={shouldShow}
        onClose={markAsSeen}
        position="bottom"
      />
    </>
  );
}
